import { useState } from 'react';
import { MessageSquarePlus, X, Send, CheckCircle } from 'lucide-react';
import apiService from '../../services/apiService';

const CATEGORIES = [
  { value: 'bug',     label: 'Bug' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'question',label: 'Question' },
  { value: 'other',   label: 'Other' },
];

const EMPTY = { title: '', body: '', category: 'bug' };

export default function ReportIssueButton() {
  const [open, setOpen]         = useState(false);
  const [form, setForm]         = useState(EMPTY);
  const [status, setStatus]     = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [issueNum, setIssueNum] = useState(null);

  function reset() {
    setForm(EMPTY);
    setStatus('idle');
    setErrorMsg('');
    setIssueNum(null);
  }

  function close() {
    setOpen(false);
    setTimeout(reset, 300);
  }

  async function submit(e) {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      const result = await apiService.reportIssue(form);
      setIssueNum(result.issueNumber);
      setStatus('success');
    } catch (err) {
      setErrorMsg(err.message || 'Failed to submit. Please try again.');
      setStatus('error');
    }
  }

  return (
    <>
      <button
        onClick={() => { reset(); setOpen(true); }}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-blue-600 text-white shadow-lg shadow-blue-500/30 hover:bg-blue-700 hover:shadow-blue-500/50 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 text-sm font-medium"
        title="Report an issue or suggest a feature"
      >
        <MessageSquarePlus size={18} />
        <span className="hidden sm:inline">Report Issue</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={e => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600">
              <h2 className="text-white font-semibold flex items-center gap-2">
                <MessageSquarePlus size={18} />
                Report an Issue
              </h2>
              <button onClick={close} className="text-white/70 hover:text-white transition-colors rounded-lg p-0.5 hover:bg-white/10">
                <X size={18} />
              </button>
            </div>

            {/* Success */}
            {status === 'success' ? (
              <div className="px-6 py-10 flex flex-col items-center gap-3 text-center">
                <CheckCircle className="text-green-500" size={52} />
                <p className="text-slate-800 font-semibold text-lg">Issue #{issueNum} submitted!</p>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Our AI triage bot will review it and apply labels automatically within a minute.
                </p>
                <button
                  onClick={close}
                  className="mt-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="px-6 py-5 space-y-4">

                {/* Category pills */}
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Category</p>
                  <div className="flex gap-2 flex-wrap">
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, category: cat.value }))}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          form.category === cat.value
                            ? 'bg-blue-600 text-white shadow-sm shadow-blue-200'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    minLength={5}
                    maxLength={120}
                    value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="Short, descriptive title…"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    minLength={10}
                    maxLength={2000}
                    rows={4}
                    value={form.body}
                    onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                    placeholder="What happened? What did you expect? Steps to reproduce…"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow resize-none"
                  />
                  <p className="text-xs text-slate-400 mt-1 text-right">{form.body.length}/2000</p>
                </div>

                {/* Error banner */}
                {status === 'error' && (
                  <p className="text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">
                    {errorMsg}
                  </p>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-1 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={close}
                    className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  >
                    {status === 'loading' ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        <Send size={15} />
                        Submit
                      </>
                    )}
                  </button>
                </div>

              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
